import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { StandardFonts } from 'pdf-lib';
import { tap } from 'rxjs/operators';
import { FileHelper } from '../helpers';
import { Agency, CourseModule, UserCourseProgress } from '../models';

@Injectable({
  providedIn: 'root'
})
export class CoursesService {

  courses: any[] = [];
  userCourses = false;

  // Current course and the list of courses, will change with further functionality
  currentCourse: any = {};
  currentModule: any;
  rentWiseAgency: any = {};
  rentWiseUserID = '';

  constructor(private http: HttpClient) { }

  // Return a copy of the courses TODO: not being used right now
  getCourses() {
    return this.courses.slice();
  }

  reloadCurrentCourse() {
    if (this.currentCourse) {
      if (this.courses.includes(this.currentCourse)) {
        // already up-to-date
      }
      else {
        this.setCurrentCourseBy(this.currentCourse._id)
      }
    }
    else {
      this.currentCourse = null;
      this.currentModule = null;
    }

    if (this.currentModule) {
      this.setCurrentModuleBy(this.currentModule.moduleID);
    }
    else {
      this.currentModule = null;
    }
  }

  setCurrentCourseBy(id: string) {
    this.currentCourse = this.courses.find(by => by._id === id);

    if (!this.currentCourse) {
      this.currentCourse = this.userCourses
        ? this.courses.find(course => course.courseID === id)
        : this.courses.find(course => course.title === id);
    }
  }

  setCurrentModuleBy(moduleID: string) {
    this.currentModule = this.currentCourse ? (this.currentCourse.modules as CourseModule[]).find(m => m.moduleID === moduleID) : null;
  }

  // TODO: rename FHE course to HPE on backend and use courseID instead
  getName(course: any) {
    if (!course) {
      return '';
    }

    const courseID = course.courseID || course.title;

    // FHE was renamed to HPE but we use FHE as the courseID in the backend, so need to manually display it instead of courseID
    if (courseID === 'Fair Housing Education') {
      return 'Housing Provider Education';
    }
    else {
      return courseID;
    }
  }

  async generateRentwiseCertificate(agency: Agency, user: any, completionDate: Date) {
    // Grab the template pdf
    // const url = '../../../../../assets/certificate-blank.pdf';
    const url = '/assets/certificate-blank.pdf'; // template
    const pdfDoc = await FileHelper.loadPdfDoc(url);

    // Embed the Times Roman font
    const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);

    // Grab first (and only) page of the document
    const page = pdfDoc.getPages()[0];

    // Get the width and height of the page
    const { width, height } = page.getSize();

    // Draw the user's name
    var fontSize = 30;
    const name = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
    var nameWidth = timesRomanFont.widthOfTextAtSize(name, fontSize);
    page.drawText(name, {
      x: width/2 - nameWidth/2,
      y: height - 4.2 * fontSize,
      size: fontSize,
      font: timesRomanFont,
    });

    // Draw the date of completion
    const dateString = completionDate ? moment(completionDate).format('MMMM D, YYYY') : 'N/A'; // October 1, 2022
    const dateWidth = timesRomanFont.widthOfTextAtSize(dateString, fontSize);
    page.drawText(dateString, {
      x: width/2 - dateWidth/2,
      y: height - 9 * fontSize,
      size: fontSize,
      font: timesRomanFont,
    });

    // Coordinator name
    fontSize = 12;
    const coordinatorName = agency.coordinatorName;
    page.drawText(coordinatorName, {
      x: 215,
      y: height - 35.75 * fontSize,
      size: fontSize,
      font: timesRomanFont,
    });

    // Agency Name
    fontSize = 12;
    const agencyName = agency.agencyName;
    page.drawText(agencyName, {
      x: 150,
      y: height - 37.15 * fontSize,
      size: fontSize,
      font: timesRomanFont,
    });

    // Agency phone
    fontSize = 12;
    const phone = agency.phone;
    page.drawText(phone, {
      x: 216,
      y: height -  38.5 * fontSize,
      size: fontSize,
      font: timesRomanFont,
    });

    return pdfDoc;
  }

  // Send request to backend with a course to enroll
  enrollUserInCourse(course: any, user: any) {
    return this.http.post('api/v1/courses/enroll', { course: course, user: user });
  }

  // Send request to backend to get courses for a user
  getUserCourses(query?: Indexable) {
    let queryParams = new HttpParams({ fromObject: query });

    return this.http.get('api/v1/courses/self', { params: queryParams });
  }

  getAllCourses(query?: Indexable) {
    let queryParams = new HttpParams({ fromObject: query });

    return this.http.get<any>('api/v1/courses/get-all', { params: queryParams });
  }

  getRentwiseEvaluations(query?: Indexable) {
    let queryParams = new HttpParams({ fromObject: query });

    return this.http.get('api/v1/courses/evaluations', { params: queryParams });
  }

  // Update furthest module reached
  updateFurthestModule() {
    // Update module to + 1
    this.currentCourse.furthestModuleID = this.currentCourse.furthestModuleID + 1;
    this.currentCourse.furthestPageID = 0;

    const data = {
      courseID:         this.currentCourse._id,
      furthestModuleID: this.currentCourse.furthestModuleID,
      furthestPageID:   this.currentCourse.furthestPageID,
    };

    const currentCourse = this.currentCourse;

    return this.http.post<any>('api/v1/courses/updateProgress', data).pipe(
      tap(res => {
        Object.assign(currentCourse, res.progress);
      })
    );
  }

  updateFurthestPage(number: number) {
    this.currentCourse.furthestPageID = number;

    const data = {
      courseID:         this.currentCourse._id,
      furthestModuleID: this.currentCourse.furthestModuleID,
      furthestPageID:   this.currentCourse.furthestPageID,
    };

    const currentCourse = this.currentCourse;

    return this.http.post<any>('api/v1/courses/updateProgress', data).pipe(
      tap(res => {
        Object.assign(currentCourse, res.progress);
      })
    );
  }

  sendPreEvaluationForm(course: UserCourseProgress, data: any) {
    const _id = course._id;
    const courseID = course.courseID;
    const agencyID = course.agencyID;
    return this.http.post('api/v1/courses/pre-evaluation', { _id, courseID, agencyID, form: data });
  }

  sendPostEvaluationForm(course: UserCourseProgress, data: any) {
    const _id = course._id;
    const courseID = course.courseID;
    const agencyID = course.agencyID;
    return this.http.post('api/v1/courses/post-evaluation', { _id, courseID, agencyID, form: data });
  }

  // Add course completion date for printing on certificate
  markAsCompleted() {
    this.currentCourse.completionDate = new Date();

    return this.http.post('api/v1/courses/markAsCompleted', { courseID: this.currentCourse._id });
  }

  getCompletionDate() {
    return this.currentCourse.completionDate;
  }

   // Get all agencies
   getAgencies() {
    return this.http.get('api/v1/agencies/get');
  }

   // Get all active agencies
   getActiveAgencies() {
    return this.http.get('api/v1/agencies/getActive');
  }
}
